// frontend/src/pages/Settings/AdminSettings.jsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAdmins, createAdmin, updateAdmin } from '../../api/admin.api.js';
import { getEntities } from '../../api/entity.api.js';
import { PageHeader, Spinner, EmptyState, ErrorMessage, FormField } from '../../components/ui';
import { Plus, Pencil, X } from 'lucide-react';

// ─── Modal ────────────────────────────────────────────────────────────────────

function AdminModal({ admin, entities, onClose, onSaved }) {
  const isEdit = !!admin;
  const [form, setForm] = useState({
    name:           admin?.name           ?? '',
    email:          admin?.email          ?? '',
    password:       '',
    phone:          admin?.phone          ?? '',
    role:           admin?.role           ?? 'SUB_ACCESS',
    scopeType:      admin?.scopeType      ?? 'GLOBAL',
    scopeEntityIds: admin?.scopeEntityIds ?? [],
    isActive:       admin?.isActive       ?? true,
  });
  const [error, setError] = useState(null);

  const save = useMutation({
    mutationFn: () => isEdit
      ? updateAdmin(admin.id, form)
      : createAdmin(form),
    onSuccess: () => { onSaved(); onClose(); },
    onError:   (err) => setError(err),
  });

  function set(field) { return (e) => setForm((f) => ({ ...f, [field]: e.target.value })); }

  function toggleEntity(entityId) {
    setForm((f) => ({
      ...f,
      scopeEntityIds: f.scopeEntityIds.includes(entityId)
        ? f.scopeEntityIds.filter((x) => x !== entityId)
        : [...f.scopeEntityIds, entityId],
    }));
  }

  const showScope = form.role === 'SUB_ACCESS' && form.scopeType === 'SUBSIDIARY';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">
            {isEdit ? 'Edit Admin User' : 'New Admin User'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {error && <ErrorMessage error={error} />}

          <FormField label="Name" required>
            <input className="input" value={form.name} onChange={set('name')} required />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Email" required={!isEdit}>
              <input className="input" type="email" value={form.email}
                onChange={set('email')} disabled={isEdit} required={!isEdit} />
            </FormField>
            <FormField label={isEdit ? 'New Password (optional)' : 'Password'} required={!isEdit}>
              <input className="input" type="password" value={form.password}
                onChange={set('password')} placeholder={isEdit ? 'Leave blank to keep' : ''} />
            </FormField>
          </div>

          <FormField label="Phone">
            <input className="input" type="tel" value={form.phone} onChange={set('phone')} />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Role" required>
              <select
                className="input"
                value={form.role}
                onChange={(e) => {
                  const role = e.target.value;
                  setForm((f) => ({
                    ...f,
                    role,
                    scopeType: role === 'MASTER' ? 'GLOBAL' : f.scopeType,
                    scopeEntityIds: role === 'MASTER' ? [] : f.scopeEntityIds,
                  }));
                }}
              >
                <option value="MASTER">Master</option>
                <option value="SUB_ACCESS">Sub-Access</option>
              </select>
            </FormField>

            {form.role === 'SUB_ACCESS' && (
              <FormField label="Scope">
                <select
                  className="input"
                  value={form.scopeType}
                  onChange={(e) => setForm((f) => ({ ...f, scopeType: e.target.value, scopeEntityIds: [] }))}
                >
                  <option value="GLOBAL">Global (all entities)</option>
                  <option value="SUBSIDIARY">Subsidiary (scoped)</option>
                </select>
              </FormField>
            )}
          </div>

          {/* Entity scope selector */}
          {showScope && (
            <FormField label="Allowed Entities">
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                {entities?.map((e) => (
                  <label key={e.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.scopeEntityIds.includes(e.id)}
                      onChange={() => toggleEntity(e.id)}
                      className="rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                    />
                    <div>
                      <p className="text-sm text-gray-900">{e.name}</p>
                      {e.code && <p className="text-xs text-gray-400 font-mono">{e.code}</p>}
                    </div>
                  </label>
                ))}
                {!entities?.length && (
                  <p className="px-3 py-4 text-sm text-gray-400 text-center">
                    No entities loaded. Sync from HR first.
                  </p>
                )}
              </div>
              {form.scopeEntityIds.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">{form.scopeEntityIds.length} entity selected</p>
              )}
            </FormField>
          )}

          {isEdit && (
            <FormField label="Status">
              <select
                className="input"
                value={String(form.isActive)}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.value === 'true' }))}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </FormField>
          )}
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="btn-primary flex-1"
          >
            {save.isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Admin'}
          </button>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminSettings() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null); // null | 'new' | admin object

  const { data: admins, isLoading } = useQuery({
    queryKey: ['admins'],
    queryFn:  () => getAdmins(),
  });

  const { data: entities } = useQuery({
    queryKey: ['entities'],
    queryFn:  () => getEntities(),
  });

  function onSaved() { qc.invalidateQueries({ queryKey: ['admins'] }); }

  return (
    <div>
      <PageHeader
        title="Admin Users"
        subtitle="Manage access and entity scope for all admin users."
        action={
          <button
            onClick={() => setModal('new')}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <Plus size={16} /> New Admin
          </button>
        }
      />

      {isLoading ? <Spinner /> : !admins?.length ? (
        <EmptyState title="No admin users found" />
      ) : (
        <div className="card divide-y divide-gray-100">
          {admins.map((a) => (
            <div key={a.id} className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-4">
                <div className="w-9 h-9 rounded-full bg-brand-500 flex items-center justify-center
                                text-white text-sm font-semibold flex-shrink-0">
                  {a.name[0].toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{a.name}</p>
                  <p className="text-xs text-gray-500">{a.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="text-right">
                  <span className={`badge ${a.role === 'MASTER' ? 'bg-brand-100 text-brand-700' : 'bg-gray-100 text-gray-600'}`}>
                    {a.role === 'MASTER' ? 'Master' : 'Sub-Access'}
                  </span>
                  {a.scopeType === 'SUBSIDIARY' && (
                    <p className="text-xs text-amber-600 mt-0.5">
                      Scoped · {a.scopeEntityIds.length} entities
                    </p>
                  )}
                </div>
                {!a.isActive && (
                  <span className="badge bg-red-100 text-red-600">Inactive</span>
                )}
                <button
                  onClick={() => setModal(a)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Pencil size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <AdminModal
          admin={modal === 'new' ? null : modal}
          entities={entities}
          onClose={() => setModal(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
